/**
 * Klient API Portfolio HQ.
 *
 * Sesja siedzi w ciasteczku `httpOnly` z ochroną CSRF (double submit), więc
 * każde żądanie idzie z `credentials: "include"`, a przy metodach zmieniających
 * stan dokładamy token CSRF z ciasteczka czytelnego dla skryptu. Token dostępu
 * nigdy nie przechodzi przez `localStorage` — w systemie, w którym leżą finanse
 * całego portfolio, jeden XSS w dowolnej zależności nie może kończyć się
 * wykradzeniem sesji.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
  }

  get isUnauthorized() {
    return this.status === 401;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match?.[2] ? decodeURIComponent(match[2]) : null;
}

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
};

/*
 * Token dostępu żyje 30 minut, odświeżający 14 dni. Bez odświeżania każda
 * strona po pół godzinie pokazywała „Nie udało się pobrać danych", choć sesja
 * była ważna. Przy 401 próbujemy więc raz odświeżyć i powtórzyć żądanie; gdy
 * odświeżenie też się nie uda, sesja naprawdę się skończyła i aplikacja wraca
 * do logowania.
 */
const WITHOUT_REFRESH = new Set(["/auth/login", "/auth/refresh", "/auth/logout"]);

let refreshing: Promise<boolean> | null = null;
let sessionLost: () => void = () => {};

/** Co zrobić, gdy sesji nie da się już odświeżyć. Ustawia to `main.tsx`. */
export function onSessionLost(handler: () => void) {
  sessionLost = handler;
}

function refreshSession(): Promise<boolean> {
  // Jedno odświeżenie na raz: kilka zapytań strony dostaje 401 jednocześnie,
  // a każde odświeżenie osobno unieważniałoby ciasteczka poprzedniego.
  refreshing ??= fetch("/api/v1/auth/refresh", {
    method: "POST",
    credentials: "include",
    headers: csrfHeader("csrf_refresh_token"),
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

function csrfHeader(cookie: string): Record<string, string> {
  const csrf = readCookie(cookie);
  return csrf ? { "X-CSRF-TOKEN": csrf } : {};
}

async function request<T>(path: string, options: RequestOptions = {}, retried = false): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };

  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET") Object.assign(headers, csrfHeader("csrf_access_token"));

  const response = await fetch(`/api/v1${path}`, {
    method,
    headers,
    credentials: "include",
    signal: options.signal,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (response.status === 401 && !retried && !WITHOUT_REFRESH.has(path)) {
    if (await refreshSession()) return request<T>(path, options, true);
    sessionLost();
  }

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (payload as { error?: { message?: string; code?: string } } | null)?.error;
    throw new ApiError(
      error?.message ?? `Błąd ${response.status}`,
      response.status,
      error?.code ?? "unknown",
    );
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  // Treść przy DELETE jest potrzebna tam, gdzie usuwany byt nie ma
  // identyfikatora nadającego się do adresu — etykieta jest tekstem pisanym
  // przez człowieka i może zawierać spację albo ukośnik.
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: "DELETE", body }),
};
